import { format } from 'date-fns';
import type { ScheduleShiftImportColumnMapping } from '../types';

export type CellMatrix = (string | number | undefined)[][];

export type ImportPatternId = 'flat-columns' | 'grid-short-time' | 'grid-verbose-time';

export const FLAT_HEADERS = {
  employee: 'Employee',
  date: 'Date',
  startTime: 'Start time',
  endTime: 'End time',
} as const;

export const FLAT_COLUMN_MAPPING: ScheduleShiftImportColumnMapping = {
  employeeName: FLAT_HEADERS.employee,
  date: FLAT_HEADERS.date,
  startTime: FLAT_HEADERS.startTime,
  endTime: FLAT_HEADERS.endTime,
};

export type GridWeekBlock = {
  label: string;
  weekStart: string;
  weekEnd: string;
  dateRowIndex: number;
  dataStartRowIndex: number;
  dataEndRowIndex: number;
  dayColumnIndices: number[];
};

export type ImportPatternResult = {
  patternId: ImportPatternId;
  patternName: string;
  confidence: number;
  rows: Array<{ rowNumber: number; cells: Record<string, string> }>;
  mapping: ScheduleShiftImportColumnMapping;
  gridWeekBlocks?: GridWeekBlock[];
  selectedWeekBlock?: GridWeekBlock;
};

const WEEKDAY_NAMES = new Set([
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
]);

const SKIP_SHIFT_VALUES = new Set([
  '',
  'n/a',
  'na',
  'h/days',
  'h/day',
  'a/l',
  'al',
  'off',
]);

function cellToString(v: string | number | undefined | null): string {
  if (v === undefined || v === null) return '';
  return String(v).trim();
}

function parseExcelSerialDate(n: number): Date | null {
  if (!Number.isFinite(n) || n < 1 || n >= 60000) return null;
  const epoch = new Date(Date.UTC(1899, 11, 30));
  return new Date(epoch.getTime() + n * 86400000);
}

function parseCalendarDate(day: number, month: number, year: number): Date | null {
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) return null;
  const d = new Date(Date.UTC(year, month - 1, day));
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) return null;
  return d;
}

/** Kdoc rota uses D/M/Y (e.g. 1/6/2026 = 1 June). US M/D/Y is tried only when D/M/Y is invalid. */
function parseSlashSeparatedDate(t: string): Date | null {
  const m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (!m) return null;
  const first = parseInt(m[1]!, 10);
  const second = parseInt(m[2]!, 10);
  let year = parseInt(m[3]!, 10);
  if (year < 100) year += year >= 70 ? 1900 : 2000;

  const dmy = parseCalendarDate(first, second, year);
  if (dmy) return dmy;

  return parseCalendarDate(second, first, year);
}

export function parseImportDate(v: string | number | undefined | null): Date | null {
  if (v === undefined || v === null) return null;
  if (typeof v === 'number') {
    const fromSerial = parseExcelSerialDate(v);
    if (fromSerial) return fromSerial;
  }
  const t = cellToString(v);
  if (!t) return null;
  const num = Number(t.replace(/,/g, ''));
  if (!Number.isNaN(num) && Number.isFinite(num) && num > 10 && num < 60000) {
    const fromSerial = parseExcelSerialDate(num);
    if (fromSerial) return fromSerial;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) {
    const d = new Date(t.replace(' ', 'T'));
    if (!Number.isNaN(d.getTime())) return d;
  }
  const slash = parseSlashSeparatedDate(t);
  if (slash) return slash;
  const d2 = new Date(t);
  if (!Number.isNaN(d2.getTime())) return d2;
  return null;
}

function isSkipShiftCell(raw: string): boolean {
  const t = raw.trim().toLowerCase().replace(/\s+/g, '');
  if (SKIP_SHIFT_VALUES.has(t)) return true;
  if (/^wk\s*\d+$/i.test(raw.trim())) return true;
  return false;
}

function isSectionHeader(name: string): boolean {
  const t = name.trim().toLowerCase();
  if (!t) return true;
  if (WEEKDAY_NAMES.has(t)) return true;
  if (/^kdoc|^rota|^tc\//i.test(t)) return true;
  if (/^\d+$/.test(t)) return true;
  if (t.length > 40) return true;
  return false;
}

function parseVerboseTimeRange(raw: string): { start: string; end: string; overnight: boolean } | null {
  let t = raw.trim();
  const suffixMatch = t.match(/^(.+?)\s+[A-Z]{2,}\s*$/i);
  if (suffixMatch) t = suffixMatch[1]!.trim();

  const lower = t.toLowerCase();
  if (lower === 'midnight-9am' || lower === 'midnight - 9am') {
    return { start: '00:00', end: '09:00', overnight: false };
  }

  const m = t.match(
    /^(\d{1,2}(?:\.\d{1,2})?\s*(?:am|pm)?)\s*[-–—]\s*(midnight|\d{1,2}(?:\.\d{1,2})?\s*(?:am|pm)?)$/i
  );
  if (!m) return null;

  const parsePart = (part: string, defaultPm: boolean): { hour: number; minute: number } | null => {
    const p = part.trim().toLowerCase();
    if (p === 'midnight') return { hour: 0, minute: 0 };
    const m2 = p.match(/^(\d{1,2})(?:\.(\d{1,2}))?\s*(am|pm)?$/i);
    if (!m2) return null;
    let hour = parseInt(m2[1]!, 10);
    const minute = m2[2] ? parseInt(m2[2].padEnd(2, '0').slice(0, 2), 10) : 0;
    const ampm = m2[3]?.toLowerCase();
    if (ampm === 'pm' && hour < 12) hour += 12;
    if (ampm === 'am' && hour === 12) hour = 0;
    if (!ampm && defaultPm && hour <= 11) hour += 12;
    if (!ampm && !defaultPm && hour === 12) hour = 0;
    return { hour, minute };
  };

  const startPart = parsePart(m[1]!, false);
  let endPart = parsePart(m[2]!, true);
  if (!startPart || !endPart) return null;

  if (m[2]!.toLowerCase() === 'midnight') {
    endPart = { hour: 0, minute: 0 };
  }

  const start = `${String(startPart.hour).padStart(2, '0')}:${String(startPart.minute).padStart(2, '0')}`;
  const end = `${String(endPart.hour).padStart(2, '0')}:${String(endPart.minute).padStart(2, '0')}`;
  const overnight =
    endPart.hour < startPart.hour ||
    (endPart.hour === startPart.hour && endPart.minute <= startPart.minute) ||
    m[2]!.toLowerCase() === 'midnight';
  return { start, end, overnight };
}

function parseShortTimeRange(raw: string): { start: string; end: string; overnight: boolean } | null {
  const t = raw.trim().toLowerCase().replace(/\s+/g, '');
  const m = t.match(/^(\d{1,2})(?:\.(\d{1,2}))?[-–—](\d{1,2})(?:\.(\d{1,2}))?$/);
  if (!m) return null;

  const a = parseInt(m[1]!, 10);
  const b = parseInt(m[3]!, 10);
  const aMin = m[2] ? parseInt(m[2].padEnd(2, '0').slice(0, 2), 10) : 0;
  const bMin = m[4] ? parseInt(m[4].padEnd(2, '0').slice(0, 2), 10) : 0;

  let startH: number;
  let endH: number;

  if (a === b) {
    startH = a;
    endH = a + 12;
  } else if (b > a) {
    startH = a;
    if (b <= 7) endH = b + 12;
    else endH = b;
  } else {
    if (a === 12) startH = 12;
    else if (a >= 8) startH = a;
    else if (a >= 4) startH = a + 12;
    else startH = a;

    if (b === 12) endH = 24;
    else endH = b + 12;
  }

  if (endH >= 24) {
    return {
      start: `${String(startH).padStart(2, '0')}:${String(aMin).padStart(2, '0')}`,
      end: '00:00',
      overnight: true,
    };
  }

  const overnight = endH < startH;
  return {
    start: `${String(startH).padStart(2, '0')}:${String(aMin).padStart(2, '0')}`,
    end: `${String(endH).padStart(2, '0')}:${String(bMin).padStart(2, '0')}`,
    overnight,
  };
}

function parseTimeRange(
  raw: string,
  mode: 'short' | 'verbose'
): { start: string; end: string; overnight: boolean } | null {
  if (mode === 'verbose') {
    const v = parseVerboseTimeRange(raw);
    if (v) return v;
    return parseShortTimeRange(raw);
  }
  const s = parseShortTimeRange(raw);
  if (s) return s;
  return parseVerboseTimeRange(raw);
}

function findGridWeekBlocks(matrix: CellMatrix): GridWeekBlock[] {
  const blocks: GridWeekBlock[] = [];

  for (let r = 0; r < matrix.length; r++) {
    const row = matrix[r] ?? [];
    const dateCols: { col: number; date: Date }[] = [];

    for (let c = 0; c < Math.min(row.length, 15); c++) {
      if (c === 0) continue;
      const d = parseImportDate(row[c]);
      if (d && d.getFullYear() >= 2020 && d.getFullYear() <= 2100) {
        dateCols.push({ col: c, date: d });
      }
    }

    if (dateCols.length < 4) continue;

    dateCols.sort((x, y) => x.date.getTime() - y.date.getTime());
    const weekStart = format(dateCols[0]!.date, 'yyyy-MM-dd');
    const weekEnd = format(dateCols[dateCols.length - 1]!.date, 'yyyy-MM-dd');

    let dataStart = r + 1;
    let dataEnd = r + 1;
    for (let dr = r + 1; dr < matrix.length; dr++) {
      const dataRow = matrix[dr] ?? [];
      const name = cellToString(dataRow[0]);
      const nextRowDateCount = (matrix[dr + 1] ?? []).slice(1, 10).filter((c) => {
        const d = parseImportDate(c);
        return d && d.getFullYear() >= 2020;
      }).length;

      if (nextRowDateCount >= 4) {
        dataEnd = dr - 1;
        break;
      }

      const hasShift = dateCols.some(({ col }) => !isSkipShiftCell(cellToString(dataRow[col])));
      const hasName = name.length > 0 && !isSectionHeader(name);

      if (!hasName && !hasShift) {
        if (dr > dataStart) {
          dataEnd = dr - 1;
          break;
        }
      } else {
        dataEnd = dr;
      }
    }

    if (dataEnd >= dataStart) {
      blocks.push({
        label: `${weekStart} – ${weekEnd}`,
        weekStart,
        weekEnd,
        dateRowIndex: r,
        dataStartRowIndex: dataStart,
        dataEndRowIndex: dataEnd,
        dayColumnIndices: dateCols.map((x) => x.col),
      });
    }
  }

  return blocks;
}

function unpivotGridBlock(
  matrix: CellMatrix,
  block: GridWeekBlock,
  timeMode: 'short' | 'verbose',
  allowedWeekStart?: string,
  allowedWeekEnd?: string
): Array<{ rowNumber: number; cells: Record<string, string> }> {
  const rows: Array<{ rowNumber: number; cells: Record<string, string> }> = [];
  const dateRow = matrix[block.dateRowIndex] ?? [];
  let ordinal = 0;

  for (let r = block.dataStartRowIndex; r <= block.dataEndRowIndex; r++) {
    const dataRow = matrix[r] ?? [];
    const employeeName = cellToString(dataRow[0]);
    if (!employeeName || isSectionHeader(employeeName)) continue;

    for (const col of block.dayColumnIndices) {
      const shiftRaw = cellToString(dataRow[col]);
      if (isSkipShiftCell(shiftRaw)) continue;

      const shiftDate = parseImportDate(dateRow[col]);
      if (!shiftDate) continue;

      const dateStr = format(shiftDate, 'yyyy-MM-dd');
      if (allowedWeekStart && dateStr < allowedWeekStart) continue;
      if (allowedWeekEnd && dateStr > allowedWeekEnd) continue;

      const times = parseTimeRange(shiftRaw, timeMode);
      if (!times) continue;

      ordinal += 1;
      rows.push({
        rowNumber: ordinal,
        cells: {
          [FLAT_HEADERS.employee]: employeeName,
          [FLAT_HEADERS.date]: dateStr,
          [FLAT_HEADERS.startTime]: times.start,
          [FLAT_HEADERS.endTime]: times.end,
        },
      });
    }
  }

  return rows;
}

function scoreGridPattern(matrix: CellMatrix, timeMode: 'short' | 'verbose'): number {
  const blocks = findGridWeekBlocks(matrix);
  if (blocks.length === 0) return 0;

  let parsed = 0;
  let attempted = 0;
  for (const block of blocks.slice(0, 2)) {
    const dateRow = matrix[block.dateRowIndex] ?? [];
    for (let r = block.dataStartRowIndex; r <= Math.min(block.dataEndRowIndex, block.dataStartRowIndex + 25); r++) {
      const dataRow = matrix[r] ?? [];
      if (!cellToString(dataRow[0]) || isSectionHeader(cellToString(dataRow[0]))) continue;
      for (const col of block.dayColumnIndices) {
        const shiftRaw = cellToString(dataRow[col]);
        if (isSkipShiftCell(shiftRaw)) continue;
        attempted += 1;
        if (parseTimeRange(shiftRaw, timeMode)) parsed += 1;
      }
    }
  }

  if (attempted === 0) return 0;
  return 0.4 + 0.6 * (parsed / attempted);
}

function tryFlatPattern(matrix: CellMatrix): ImportPatternResult | null {
  if (!matrix.length) return null;

  const rawHeaders = (matrix[0] ?? []).map((c) => cellToString(c));
  if (!rawHeaders.some(Boolean)) return null;

  const headerSynonyms: Record<keyof ScheduleShiftImportColumnMapping, RegExp[]> = {
    employeeName: [/employee/i, /staff/i, /name/i, /worker/i],
    date: [/^date$/i, /shift\s*date/i, /^day$/i],
    startTime: [/start/i, /from/i, /begin/i],
    endTime: [/end/i, /to/i, /finish/i, /until/i],
  };

  const mapping: ScheduleShiftImportColumnMapping = {
    employeeName: '',
    date: '',
    startTime: '',
    endTime: '',
  };

  for (const key of Object.keys(headerSynonyms) as (keyof ScheduleShiftImportColumnMapping)[]) {
    const hit = rawHeaders.find((h) => h && headerSynonyms[key].some((rx) => rx.test(h)));
    if (hit) mapping[key] = hit;
  }

  const filled = Object.values(mapping).filter(Boolean).length;
  if (filled < 3) return null;

  const rows: Array<{ rowNumber: number; cells: Record<string, string> }> = [];
  let ordinal = 0;
  for (let r = 1; r < matrix.length; r++) {
    const line = matrix[r] ?? [];
    const cells: Record<string, string> = {};
    let any = false;
    rawHeaders.forEach((h, i) => {
      if (!h) return;
      const v = cellToString(line[i]);
      cells[h] = v;
      if (v) any = true;
    });
    if (any) {
      ordinal += 1;
      rows.push({ rowNumber: ordinal, cells });
    }
  }

  if (rows.length === 0) return null;

  return {
    patternId: 'flat-columns',
    patternName: 'Column headers (Employee, Date, Start, End)',
    confidence: 0.3 + filled * 0.15,
    rows,
    mapping,
  };
}

function tryGridPattern(
  matrix: CellMatrix,
  timeMode: 'short' | 'verbose',
  patternId: ImportPatternId,
  patternName: string,
  allowedWeekStart?: string,
  allowedWeekEnd?: string
): ImportPatternResult | null {
  const confidence = scoreGridPattern(matrix, timeMode);
  if (confidence < 0.5) return null;

  const blocks = findGridWeekBlocks(matrix);
  if (blocks.length === 0) return null;

  let selected = blocks[0]!;
  if (allowedWeekStart) {
    const hit = blocks.find(
      (b) => allowedWeekStart >= b.weekStart && allowedWeekStart <= b.weekEnd
    );
    if (hit) selected = hit;
  }

  const rows = unpivotGridBlock(matrix, selected, timeMode, allowedWeekStart, allowedWeekEnd);

  return {
    patternId,
    patternName,
    confidence,
    rows,
    mapping: { ...FLAT_COLUMN_MAPPING },
    gridWeekBlocks: blocks,
    selectedWeekBlock: selected,
  };
}

/** Try import patterns in specificity order; returns best match above minConfidence. */
export function detectScheduleImportPattern(
  matrix: CellMatrix,
  options?: { allowedWeekStart?: string; allowedWeekEnd?: string; minConfidence?: number }
): ImportPatternResult | null {
  const minConfidence = options?.minConfidence ?? 0.5;
  const candidates: ImportPatternResult[] = [];

  const gridVerbose = tryGridPattern(
    matrix,
    'verbose',
    'grid-verbose-time',
    'Weekly rota grid (e.g. 8am–4pm)',
    options?.allowedWeekStart,
    options?.allowedWeekEnd
  );
  if (gridVerbose) candidates.push(gridVerbose);

  const gridShort = tryGridPattern(
    matrix,
    'short',
    'grid-short-time',
    'Weekly rota grid (e.g. 6–12)',
    options?.allowedWeekStart,
    options?.allowedWeekEnd
  );
  if (gridShort) candidates.push(gridShort);

  const flat = tryFlatPattern(matrix);
  if (flat) candidates.push(flat);

  candidates.sort((a, b) => b.confidence - a.confidence);
  const best = candidates[0];
  if (!best || best.confidence < minConfidence) return flat ?? best ?? null;
  return best;
}

export function unpivotGridWeekBlock(
  matrix: CellMatrix,
  block: GridWeekBlock,
  timeMode: 'short' | 'verbose',
  allowedWeekStart?: string,
  allowedWeekEnd?: string
): ImportPatternResult {
  const patternId: ImportPatternId = timeMode === 'verbose' ? 'grid-verbose-time' : 'grid-short-time';
  const rows = unpivotGridBlock(matrix, block, timeMode, allowedWeekStart, allowedWeekEnd);
  return {
    patternId,
    patternName: timeMode === 'verbose' ? 'Weekly rota grid (e.g. 8am–4pm)' : 'Weekly rota grid (e.g. 6–12)',
    confidence: 1,
    rows,
    mapping: { ...FLAT_COLUMN_MAPPING },
    gridWeekBlocks: findGridWeekBlocks(matrix),
    selectedWeekBlock: block,
  };
}

export type ImportRow = { rowNumber: number; cells: Record<string, string> };

export type OtherWeekImportSummary = {
  label: string;
  weekStart: string;
  weekEnd: string;
  shiftCount: number;
  sampleRows: ImportRow[];
};

export type MultiWeekImportSummary = {
  currentWeekCount: number;
  otherWeeks: OtherWeekImportSummary[];
  hasOtherWeeks: boolean;
  allRows: ImportRow[];
  fullRangeStart: string;
  fullRangeEnd: string;
};

function importRowDateStr(row: ImportRow, dateCol: string): string | null {
  const raw = row.cells[dateCol]?.trim();
  if (!raw) return null;
  const d = parseImportDate(raw);
  return d ? format(d, 'yyyy-MM-dd') : null;
}

function isDateInRange(dateStr: string, start: string, end: string): boolean {
  return dateStr >= start && dateStr <= end;
}

/** Unpivot every week block in a grid file, or all flat rows — no selected-week filter. */
export function collectAllImportRowsFromMatrix(
  matrix: CellMatrix,
  importPattern: ImportPatternResult,
  gridTimeMode: 'short' | 'verbose'
): ImportRow[] {
  if (importPattern.gridWeekBlocks?.length) {
    const rows: ImportRow[] = [];
    let ordinal = 0;
    for (const block of importPattern.gridWeekBlocks) {
      const blockRows = unpivotGridBlock(matrix, block, gridTimeMode);
      for (const r of blockRows) {
        ordinal += 1;
        rows.push({ rowNumber: ordinal, cells: r.cells });
      }
    }
    return rows;
  }

  if (importPattern.patternId === 'flat-columns') {
    return tryFlatPattern(matrix)?.rows ?? importPattern.rows;
  }

  return importPattern.rows;
}

/** Detect shifts outside the scheduling week and summarize them for admin confirmation. */
export function buildMultiWeekImportSummary(
  allRows: ImportRow[],
  dateCol: string,
  allowedWeekStart: string,
  allowedWeekEnd: string,
  gridWeekBlocks?: GridWeekBlock[]
): MultiWeekImportSummary {
  const datedRows = allRows
    .map((row) => ({ row, dateStr: importRowDateStr(row, dateCol) }))
    .filter((x): x is { row: ImportRow; dateStr: string } => x.dateStr != null);

  const currentWeekRows = datedRows.filter(({ dateStr }) =>
    isDateInRange(dateStr, allowedWeekStart, allowedWeekEnd)
  );
  const otherRows = datedRows.filter(
    ({ dateStr }) => !isDateInRange(dateStr, allowedWeekStart, allowedWeekEnd)
  );

  const otherWeeks: OtherWeekImportSummary[] = [];

  if (otherRows.length > 0 && gridWeekBlocks?.length) {
    for (const block of gridWeekBlocks) {
      const blockOtherRows = otherRows
        .filter(({ dateStr }) => isDateInRange(dateStr, block.weekStart, block.weekEnd))
        .map(({ row }) => row);
      if (blockOtherRows.length > 0) {
        otherWeeks.push({
          label: block.label,
          weekStart: block.weekStart,
          weekEnd: block.weekEnd,
          shiftCount: blockOtherRows.length,
          sampleRows: blockOtherRows.slice(0, 5),
        });
      }
    }
  }

  if (otherRows.length > 0 && otherWeeks.length === 0) {
    const dates = otherRows.map(({ dateStr }) => dateStr).sort();
    otherWeeks.push({
      label: `${dates[0]!} – ${dates[dates.length - 1]!}`,
      weekStart: dates[0]!,
      weekEnd: dates[dates.length - 1]!,
      shiftCount: otherRows.length,
      sampleRows: otherRows.slice(0, 5).map(({ row }) => row),
    });
  }

  const allDates = datedRows.map(({ dateStr }) => dateStr).sort();

  return {
    currentWeekCount: currentWeekRows.length,
    otherWeeks,
    hasOtherWeeks: otherRows.length > 0,
    allRows,
    fullRangeStart: allDates[0] ?? allowedWeekStart,
    fullRangeEnd: allDates[allDates.length - 1] ?? allowedWeekEnd,
  };
}
